import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { WidgetInfo } from 'src/app/interfaces/widget-info';
import { WidgetInfoGroup } from 'src/app/interfaces/widget-info-group';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { UtilityService } from 'src/app/services/utility.service';

import { ApiErrorsWidgetComponent } from '../dashboard-widgets/api-errors-widget/api-errors-widget.component';
import { ExpiringInsuranceWidgetComponent } from '../dashboard-widgets/expiring-insurance-widget/expiring-insurance-widget.component';
import { PendingReviewsWidgetComponent } from '../dashboard-widgets/pending-reviews-widget/pending-reviews-widget.component';
import { QualityControlWidgetComponent } from '../dashboard-widgets/quality-control-widget/quality-control-widget.component';
import { ResolvingVendorsWidgetComponent } from '../dashboard-widgets/resolving-vendors-widget/resolving-vendors-widget.component';
import { ReviewingVendorsWidgetComponent } from '../dashboard-widgets/reviewing-vendors-widget/reviewing-vendors-widget.component';
import { VendorRenewalsWidgetComponent } from '../dashboard-widgets/vendor-renewals-widget/vendor-renewals-widget.component';
import { VendorStatusesChartWidgetComponent } from '../dashboard-widgets/vendor-statuses-chart-widget/vendor-statuses-chart-widget.component';
import { VendorSuspensionsWidgetComponent } from '../dashboard-widgets/vendor-suspensions-widget/vendor-suspensions-widget.component';

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminDashboardComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);
  widgetInfoGroup: WidgetInfoGroup = { widgetInfoCollection: [] };

  CompanyId!: number;
  constructor(
    private AuthService: AuthService,
    private CompanyVendorService: CompanyVendorService,
    private UtilityService: UtilityService
  ) {
    this.CompanyId = this.AuthService.getUserCompanyId();
  }

  ReviewingVendorsWidgetData = new Map();
  RenewingVendorsWidgetData = new Map();

  ngOnInit(): void {
    this.CompanyVendorService.getCompanyVendor(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: [],
        aliasedAttributes: [
          'nonRenewingLast30Days',
          'nonRenewingLast60Days',
          'nonRenewingLast90Days'
        ],
        aggregations: [
          [`businessInfoReviewUnstartedOrReviewing`, 'count'],
          [`ownerInfoReviewUnstartedOrReviewing`, 'count'],
          [`tradeInfoReviewUnstartedOrReviewing`, 'count'],
          [`insuranceInfoReviewUnstartedOrReviewing`, 'count'],
          [`daysInReviewingStatus`, 'avg'],

          ['businessInfoReviewUnstartedOrRenewing', 'count'],
          ['insuranceInfoReviewUnstartedOrRenewing', 'count'],
          ['ownerInfoReviewUnstartedOrRenewing', 'count'],
          ['tradeInfoReviewUnstartedOrRenewing', 'count'],
          ['daysInRenewingStatus', 'avg'],
          ['upcomingRenewal30Days', 'count'],
          ['upcomingRenewal60Days', 'count'],
          ['upcomingRenewal90Days', 'count']
        ],
        VendorId: { $ne: null }
      })
    ).subscribe({
      next: (CompanyVendor) => {
        const {
          [`avg(daysInReviewingStatus)`]: avgNumOfDaysInReviewing,
          [`count(businessInfoReviewUnstartedOrReviewing)`]:
            complianceProgressReviewingVendorInfo,
          [`count(insuranceInfoReviewUnstartedOrReviewing)`]:
            complianceProgressReviewingInsuranceInfo,
          [`count(ownerInfoReviewUnstartedOrReviewing)`]:
            complianceProgressReviewingOwnerInfo,
          [`count(tradeInfoReviewUnstartedOrReviewing)`]:
            complianceProgressReviewingTradesLicensesInfo,

          [`avg(daysInRenewingStatus)`]: avgNumOfDaysInRenewing,
          [`count(businessInfoReviewUnstartedOrRenewing)`]:
            complianceProgressRenewingVendorInfo,
          [`count(insuranceInfoReviewUnstartedOrRenewing)`]:
            complianceProgressRenewingInsuranceInfo,
          [`count(ownerInfoReviewUnstartedOrRenewing)`]:
            complianceProgressRenewingOwnerInfo,
          [`count(tradeInfoReviewUnstartedOrRenewing)`]:
            complianceProgressRenewingTradeLicensesInfo,
          [`count(upcomingRenewal30Days)`]: upcomingRenewal30Days,
          [`count(upcomingRenewal60Days)`]: upcomingRenewal60Days,
          [`count(upcomingRenewal90Days)`]: upcomingRenewal90Days,
          nonRenewingLast30Days,
          nonRenewingLast60Days,
          nonRenewingLast90Days
        } = CompanyVendor;

        this.ReviewingVendorsWidgetData.set('Business Review', {
          count: Number(complianceProgressReviewingVendorInfo),
          query: {
            literals: { $and: ['businessInfoReviewUnstartedOrReviewing'] }
          }
        });

        this.ReviewingVendorsWidgetData.set('Owner Review', {
          count: Number(complianceProgressReviewingOwnerInfo),
          query: {
            literals: { $and: ['ownerInfoReviewUnstartedOrReviewing'] }
          }
        });

        this.ReviewingVendorsWidgetData.set('Trade Review', {
          count: Number(complianceProgressReviewingTradesLicensesInfo),
          query: {
            literals: { $and: ['tradeInfoReviewUnstartedOrReviewing'] }
          }
        });

        this.ReviewingVendorsWidgetData.set('Insurance Review', {
          count: Number(complianceProgressReviewingInsuranceInfo),
          query: {
            literals: { $and: ['insuranceInfoReviewUnstartedOrReviewing'] }
          }
        });

        this.ReviewingVendorsWidgetData.set(
          'Avg. # Days in Reviewing',
          Number(avgNumOfDaysInReviewing)
        );

        this.RenewingVendorsWidgetData.set('Renewing Business Reviews', {
          count: Number(complianceProgressRenewingVendorInfo),
          query: {
            literals: { $and: ['businessInfoReviewUnstartedOrRenewing'] }
          }
        });

        this.RenewingVendorsWidgetData.set('Renewing Owner Reviews', {
          count: Number(complianceProgressRenewingOwnerInfo),
          query: {
            literals: { $and: ['ownerInfoReviewUnstartedOrRenewing'] }
          }
        });

        this.RenewingVendorsWidgetData.set('Renewing Trade Reviews', {
          count: Number(complianceProgressRenewingTradeLicensesInfo),
          query: {
            literals: { $and: ['tradeInfoReviewUnstartedOrRenewing'] }
          }
        });

        this.RenewingVendorsWidgetData.set('Renewing Insurance Reviews', {
          count: Number(complianceProgressRenewingInsuranceInfo),
          query: {
            literals: { $and: ['insuranceInfoReviewUnstartedOrRenewing'] }
          }
        });

        this.RenewingVendorsWidgetData.set(
          'Average Days In Renewing',
          Number(avgNumOfDaysInRenewing)
        );

        this.RenewingVendorsWidgetData.set('Renewing 30 Days', {
          count: Number(upcomingRenewal30Days),
          query: { literals: { $and: ['upcomingRenewal30Days'] } }
        });

        this.RenewingVendorsWidgetData.set('Renewing 60 Days', {
          count: Number(upcomingRenewal60Days),
          query: { literals: { $and: ['upcomingRenewal60Days'] } }
        });

        this.RenewingVendorsWidgetData.set('Renewing 90 Days', {
          count: Number(upcomingRenewal90Days),
          query: { literals: { $and: ['upcomingRenewal90Days'] } }
        });

        this.RenewingVendorsWidgetData.set(
          'Non-Renewing 30 Days',
          `${nonRenewingLast30Days}%`
        );
        this.RenewingVendorsWidgetData.set(
          'Non-Renewing 60 Days',
          `${nonRenewingLast60Days}%`
        );
        this.RenewingVendorsWidgetData.set(
          'Non-Renewing 90 Days',
          `${nonRenewingLast90Days}%`
        );

        this._cdr.markForCheck();
      }
    });

    Object.assign(this.widgetInfoGroup, {
      widgetInfoCollection: [
        {
          class: 'row g-1',
          show: true,
          widgetInfoCollection: [
            {
              componentType: ReviewingVendorsWidgetComponent,
              show: true,
              class: 'col-12 col-md-6',
              data: this.ReviewingVendorsWidgetData
            } as WidgetInfo,
            {
              componentType: VendorRenewalsWidgetComponent,
              show: true,
              class: 'col-12 col-md-6',
              data: this.RenewingVendorsWidgetData
            } as WidgetInfo,
            {
              componentType: QualityControlWidgetComponent,
              show: false,
              class: 'col-12 col-md-6'
            } as WidgetInfo,
            {
              componentType: ApiErrorsWidgetComponent,
              show: false,
              class: 'col-12 col-md-6'
            } as WidgetInfo
          ]
        } as WidgetInfoGroup,
        {
          class: 'row g-1 mt-3',
          show: true,
          widgetInfoCollection: [
            {
              class: 'col-12 col-sm-5',
              show: true,
              widgetInfoCollection: [
                {
                  componentType: VendorStatusesChartWidgetComponent,
                  show: false
                } as WidgetInfo,
                {
                  class: 'pt-3',
                  componentType: ResolvingVendorsWidgetComponent,
                  show: false
                } as WidgetInfo
              ]
            } as WidgetInfoGroup,
            {
              class: 'col',
              show: true,
              widgetInfoCollection: [
                {
                  componentType: VendorSuspensionsWidgetComponent,
                  show: false
                } as WidgetInfo,
                {
                  class: 'pt-3',
                  componentType: ExpiringInsuranceWidgetComponent,
                  show: false
                } as WidgetInfo
              ]
            } as WidgetInfoGroup,
            {
              componentType: PendingReviewsWidgetComponent,
              show: false,
              class: 'col-12 col-sm-3 h-100'
            } as WidgetInfo
          ]
        } as WidgetInfoGroup
      ]
    });
  }
}
